import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import Price from '@/components/ui/Price';
import RoundActionConfirmDialog, { type RoundAction } from './RoundActionConfirmDialog';
import type { PendingValidationRound } from './waiterApi';

interface Props {
  open: boolean;
  onClose: () => void;
  tableNumber: string | number;
  rounds: PendingValidationRound[];
  onValidate: (round: PendingValidationRound) => void;
  onRefuse: (round: PendingValidationRound) => void;
  /** SC-953 — « Modifier » : take the round into the waiter's own cart.
   *  Absent = no Modifier button. */
  onModify?: (round: PendingValidationRound) => void;
  /** orderUuid of a round whose validate/refuse/modify call is in flight. */
  busyOrderUuid?: string | null;
  /** Set when the last validate/refuse attempt failed — surfaced inline so a
   * rejection is never silent (mirrors the close-flow's error message). */
  error?: string | null;
}

function priceToCents(value: string): number {
  return Math.round(parseFloat(value) * 100);
}

/**
 * Spec §3.1 — tapping a table's pending_validation badge opens this sheet:
 * per-round item count + total, with Valider/Refuser (skip-validation is the
 * customer-QR path; here the waiter is validating a round some OTHER device
 * or the customer QR flow already put into pending_validation).
 *
 * SC-715 — Refuser demande une deuxième confirmation, la même que celle du
 * veilleur global : c'est le même geste irrattrapable, il ne peut pas être
 * gardé d'un seul côté. SC-953 — Valider et Modifier aussi ; Modifier met les
 * plats de la ronde dans le panier du serveur pour qu'il les corrige.
 *
 * Le serveur valide ce qu'il VOIT : la feuille liste les plats de la ronde,
 * pas seulement leur nombre. La liste est le seul ascenseur de la fenêtre —
 * l'entête tient sur une ligne pour lui laisser toute la hauteur restante —
 * et l'entête de chaque ronde reste collé en haut pendant qu'on la déroule,
 * pour que Valider / Refuser ne partent jamais hors de portée.
 */
export default function PendingRoundsSheet({
  open,
  onClose,
  tableNumber,
  rounds,
  onValidate,
  onRefuse,
  onModify,
  busyOrderUuid = null,
  error = null,
}: Props) {
  const { t } = useTranslation();
  // Le geste qui attend sa deuxième confirmation. Rien n'est envoyé tant
  // qu'il est posé : c'est le second appui qui agit.
  const [pending, setPending] = useState<{ action: RoundAction; round: PendingValidationRound } | null>(
    null,
  );
  // Quel geste est parti : `busyOrderUuid` dit QUE quelque chose est en vol, pas
  // QUOI. Sans ça, les boutons grisaient ensemble sans rien dire — sur un
  // réseau de salle, rien ne prouvait que l'appui avait été pris.
  const [sentAction, setSentAction] = useState<RoundAction | null>(null);
  useEffect(() => {
    if (!busyOrderUuid) setSentAction(null);
  }, [busyOrderUuid]);

  const confirmPending = () => {
    const target = pending;
    setPending(null);
    if (!target) return;
    setSentAction(target.action);
    if (target.action === 'validate') onValidate(target.round);
    else if (target.action === 'refuse') onRefuse(target.round);
    else onModify?.(target.round);
  };

  const label = (round: PendingValidationRound, action: RoundAction) =>
    busyOrderUuid === round.orderUuid && sentAction === action
      ? t('waiter.notes.online.actionPending')
      : t(`waiter.pendingSheet.${action}`);

  return (
    <>
      <Modal
        open={open}
        // La confirmation est par-dessus : ni Échap ni le fond ne referment
        // la feuille tant qu'elle attend une réponse.
        onClose={() => {
          if (!pending) onClose();
        }}
        // Elle pose aussi son propre voile : celui-ci s'efface le temps qu'elle
        // est levée, plutôt que d'empiler un second flou plein écran.
        scrim={pending === null}
        ariaLabel={t('waiter.pendingSheet.title', { number: tableNumber })}
      >
        {/* `waiter-surface` : la feuille s'ouvre aussi depuis le veilleur, qui
            vit hors du `.waiter-root`. Sans la remise à zéro du carnet, les
            titres gardent les marges du navigateur et la liste des plats sort
            à puces, avec 40 px d'indentation. */}
        <div
          data-testid="pending-rounds-sheet"
          className="waiter-surface flex flex-col min-h-0 flex-1"
        >
          <div className="shrink-0 px-3 py-2 border-b-2 border-ink flex items-center justify-between gap-2">
            <h2 className="font-display text-base truncate">
              {t('waiter.pendingSheet.title', { number: tableNumber })}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 bg-hot-pink text-[var(--on-accent)] border-2 border-ink rounded-btn font-display text-xl leading-none w-11 h-11 flex items-center justify-center"
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
          {error && (
            <p className="shrink-0 px-3 pt-2 font-mono text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          {/* `pb-2` sans `pt` : avec un remplissage en haut, l'entête collant
              s'épingle SOUS lui et une bande de 8 px laissait défiler le texte
              des plats en clair par-dessus la barre Valider / Refuser. */}
          <div
            data-testid="pending-rounds-scroll"
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-2 space-y-2"
          >
            {rounds.length === 0 && (
              <p className="font-mono text-sm text-ink/60">{t('waiter.pendingSheet.empty')}</p>
            )}
            {rounds.map((round) => {
              const busy = busyOrderUuid === round.orderUuid;
              return (
                <section
                  key={round.orderUuid}
                  data-testid={`pending-round-${round.orderUuid}`}
                  // `first:mt-2` plutôt qu'un `pt-2` sur l'ascenseur : cette
                  // marge défile avec la carte, donc rien ne reste au-dessus de
                  // l'entête collant une fois la liste déroulée.
                  className="border-2 border-ink rounded-btn-sm bg-cream first:mt-2"
                >
                  {/* Collé en haut de l'ascenseur : sur une ronde de vingt plats,
                      les boutons restent sous le pouce sans avoir à remonter.
                      Avec Modifier (SC-953) ils sont trois : sur une ligne à
                      côté du titre ils ne laissaient plus que « Ron… » sous
                      520 px, ils passent donc dessous jusque-là. */}
                  <div
                    className={
                      onModify
                        ? 'sticky top-0 z-10 bg-cream border-b-2 border-ink px-3 py-2 flex flex-col items-stretch gap-2 min-[520px]:flex-row min-[520px]:items-center min-[520px]:justify-between'
                        : 'sticky top-0 z-10 bg-cream border-b-2 border-ink px-3 py-2 flex flex-col items-stretch gap-2 min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between'
                    }
                  >
                    <div className="min-w-0">
                      <p className="font-display text-base truncate">
                        {t('waiter.pendingSheet.roundLabel', { n: round.roundNumber })}
                      </p>
                      {/* `whitespace-nowrap` : « 40 articles · 18,90 € » se
                          pliait en laissant le « € » seul sur sa ligne, et cet
                          entête est affiché en permanence — 16 px volés aux
                          plats à chaque instant. */}
                      <p className="font-mono text-xs text-ink/70 truncate whitespace-nowrap">
                        {t('waiter.pendingSheet.itemsCount', { count: round.itemsCount })} ·{' '}
                        <Price amountCents={priceToCents(round.total)} />
                      </p>
                    </div>
                    <div
                      className={
                        onModify && !round.hasOptionAddons
                          ? 'grid grid-cols-3 gap-2 min-[520px]:flex min-[520px]:shrink-0'
                          : 'grid grid-cols-2 gap-2 min-[360px]:flex min-[360px]:shrink-0'
                      }
                    >
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`pending-refuse-${round.orderUuid}`}
                        onClick={() => setPending({ action: 'refuse', round })}
                        className="min-h-[44px] bg-paper border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm disabled:opacity-50"
                      >
                        {label(round, 'refuse')}
                      </button>
                      {/* SC-1739 — une tournée d'ajouts ne revient pas au panier : il en
                          ferait des plats entiers, au prix du catalogue. */}
                      {onModify && !round.hasOptionAddons && (
                        <button
                          type="button"
                          disabled={busy}
                          data-testid={`pending-modify-${round.orderUuid}`}
                          onClick={() => setPending({ action: 'modify', round })}
                          className="min-h-[44px] bg-[var(--w-state-seated-bg)] text-[var(--w-state-seated-fg)] border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm disabled:opacity-50"
                        >
                          {label(round, 'modify')}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy}
                        data-testid={`pending-validate-${round.orderUuid}`}
                        onClick={() => setPending({ action: 'validate', round })}
                        className="min-h-[44px] bg-mint border-2 border-ink rounded-btn-sm px-3 py-2.5 font-display text-sm disabled:opacity-50"
                      >
                        {label(round, 'validate')}
                      </button>
                    </div>
                  </div>
                  {round.items.length > 0 && (
                    <ul
                      data-testid={`pending-round-items-${round.orderUuid}`}
                      className="px-3 py-1 divide-y divide-ink/15"
                    >
                      {round.items.map((item) => (
                        <li
                          key={item.uuid}
                          data-testid={`pending-round-item-${item.uuid}`}
                          className="flex gap-2 py-1.5"
                        >
                          <span className="font-mono font-bold text-sm tabular-nums shrink-0">
                            {item.qte}×
                          </span>
                          <div className="min-w-0">
                            <p className="font-sans text-sm font-semibold">{item.name}</p>
                            {item.options.map((opt, idx) => (
                              <p key={idx} className="font-mono text-xs text-ink/70">
                                {(opt.name ?? opt.label ?? '') +
                                  (opt.qty && opt.qty > 1 ? ` ×${opt.qty}` : '')}
                              </p>
                            ))}
                            {item.comment && (
                              <p className="font-mono text-xs italic text-ink/70">{item.comment}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </Modal>
    <RoundActionConfirmDialog
      action={pending?.action ?? null}
      roundNumber={pending?.round.roundNumber ?? null}
      onCancel={() => setPending(null)}
      onConfirm={confirmPending}
      busy={busyOrderUuid !== null}
    />
    </>
  );
}
